import { env } from '@/constants';
import { ArcualViewingRoomStatus } from '@/model/activation/viewing-rooms/ViewingRoomFilters';
import { activationRoutePaths, getActivationRoute } from '@/views/activation/activation-route-paths';
import { dashboardMessages } from '@/views/dashboard/dashboard.messages';
import {
  ActivationViewType,
  getActivationService,
  type Activation,
  type ActivationResponse,
} from '@arcual/lib-app-platform';

export async function transformViewingRooms({
  items,
  accountId,
  accountName,
}: {
  items: ActivationResponse[];
  accountId: string | null;
  accountName: string;
}) {
  return Promise.all(
    items.map(async (item) => {
      const childStats = await getActivationService().getActivationStats(item.id);
      return {
        id: item.id,
        name: item.name,
        subtitle: item.operatorId === accountId ? accountName : '',
        viewingRoomURL: `/viewing-rooms/${item.id}`,
        publishStatus: item.isPublished ? dashboardMessages.published : dashboardMessages.unpublished,
        status:
          item?.childMetadata?.visibility === ArcualViewingRoomStatus.PUBLIC
            ? dashboardMessages.public
            : dashboardMessages.private,
        listingsCount: childStats.listings.total,
        href: getActivationRoute(activationRoutePaths.ID, item.id),
        operatorId: item.operatorId,
      };
    }),
  );
}

export async function buildStorefrontSummary({
  activation,
  accountId,
  accountName,
  isLightMode,
  viewingRoomPageSize,
  viewingRoomPageMap,
  isArtBasel, // TODO: remove after forking
}: {
  activation: ActivationResponse;
  accountId: string | null;
  accountName: string;
  isLightMode: boolean;
  viewingRoomPageSize: number;
  viewingRoomPageMap: Record<string, number>;
  isArtBasel?: boolean;
}) {
  const stats = await getActivationService().getActivationStats(activation.id);
  const viewingRooms = await getActivationService().getActivationViewingRooms(activation.id, {
    page: 1,
    pageSize: viewingRoomPageSize,
  });

  viewingRoomPageMap[activation.id] = 1;

  const childrenWithStats = await transformViewingRooms({
    items: viewingRooms?.items || [],
    accountId,
    accountName,
  });

  return {
    id: activation.id,
    name: activation.name,
    subtitle: `${activation.subdomain}.${activation.domain}`,
    logo: (isLightMode ? activation.logoLightUrl : activation.logoDarkUrl) ?? '',
    listingsCount: stats?.listings?.total || 0,
    children: childrenWithStats,
    viewingRooms: viewingRooms?.total || 0,
    publishStatus: activation.isPublished ? dashboardMessages.published : dashboardMessages.unpublished,
    status: isArtBasel ? dashboardMessages.publicStorefrontArtBasel : dashboardMessages.publicStorefront,
    operatorId: activation.operatorId,
    canCreateNewViewingRoom: true,
    href: getActivationRoute(activationRoutePaths.ID, activation.id),
    viewType: activation.viewType,
  };
}

export const isActivationViewingRoom = (activation?: ActivationResponse | Activation | null): boolean => {
  return Boolean(activation?.childMetadata) || activation?.viewType === ActivationViewType.VIEWING_ROOM;
};

export const buildActivationURL = (activation?: ActivationResponse | Activation | null): string => {
  if (!activation) return '';

  const domain = env.VITE_STOREFRONT_URL === 'localhost:8086' ? env.VITE_STOREFRONT_URL : activation.domain;

  if (isActivationViewingRoom(activation) && activation.childMetadata?.parentActivationSubdomain) {
    return `https://${activation.childMetadata?.parentActivationSubdomain}.${domain}/${activation?.subdomain}`;
  }
  return `https://${activation.subdomain}.${domain}`;
};

